import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Lock, Clock, AlertCircle, Upload, FileText, Mic, MessageSquare, ChevronRight } from '../../components/icons';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  fetchProgramForAssignee,
  upsertActivityProgress,
  upsertActivityResponse,
  uploadProgramFile,
  markProgramComplete,
} from '../../lib/programs';
import { ACTIVITY_TYPE_LABELS } from '../../types/program';
import type { ProgramDetail, ProgramActivity, ProgramActivityProgress, ProgramActivityResponse, ActivityStatus } from '../../types/program';

export function ProgramDetail() {
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState<ProgramDetail | null>(null);
  const [progress, setProgress] = useState<ProgramActivityProgress[]>([]);
  const [responses, setResponses] = useState<ProgramActivityResponse[]>([]);
  const [assignmentCompletedAt, setAssignmentCompletedAt] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [reflectionTexts, setReflectionTexts] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<string | null>(null);

  useEffect(() => {
    if (!assignmentId) return;
    loadData();
  }, [assignmentId]);

  const loadData = async () => {
    if (!assignmentId) return;
    try {
      const data = await fetchProgramForAssignee(assignmentId);
      if (!data) { navigate('/dashboard/programs'); return; }
      setProgram(data.program);
      setProgress(data.progress);
      setResponses(data.responses);
      setAssignmentCompletedAt(data.assignment.completedAt);

      const texts: Record<string, string> = {};
      data.responses.forEach(r => {
        if (r.responseText) texts[r.activityId] = r.responseText;
      });
      setReflectionTexts(texts);
    } catch (err) {
      console.error('Error loading program:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActivityProgress = (activityId: string): ProgramActivityProgress | undefined => {
    return progress.find(p => p.activityId === activityId);
  };

  const isActivityLocked = (index: number): boolean => {
    if (!program?.isSequential) return false;
    if (index === 0) return false;
    const prevActivity = program.activities[index - 1];
    const prevProgress = getActivityProgress(prevActivity.id);
    return prevProgress?.status !== 'completed';
  };

  const handleComplete = async (activity: ProgramActivity) => {
    setSubmitting(activity.id);
    try {
      const existing = getActivityProgress(activity.id);
      const progressId = await upsertActivityProgress(assignmentId!, activity.id, 'completed');

      if (!existing) {
        setProgress(prev => [...prev, {
          id: progressId,
          assignmentId: assignmentId!,
          activityId: activity.id,
          status: 'completed' as ActivityStatus,
          startedAt: new Date().toISOString(),
          completedAt: new Date().toISOString(),
        }]);
      } else {
        setProgress(prev => prev.map(p => p.activityId === activity.id ? { ...p, status: 'completed' as ActivityStatus, completedAt: new Date().toISOString() } : p));
      }

      // Check if all activities are now complete
      const allComplete = program?.activities.every(a => {
        if (a.id === activity.id) return true;
        return progress.find(p => p.activityId === a.id)?.status === 'completed';
      });
      if (allComplete && program && program.activities.length > 0) {
        await markProgramComplete(assignmentId!);
        setAssignmentCompletedAt(new Date().toISOString());
      }
    } catch (err) {
      console.error('Error completing activity:', err);
      alert('Failed to mark activity as complete.');
    } finally {
      setSubmitting(null);
    }
  };

  const handleSaveReflection = async (activity: ProgramActivity) => {
    const text = reflectionTexts[activity.id] || '';
    if (!text.trim()) { alert('Please write your reflection first.'); return; }
    setSubmitting(activity.id);
    try {
      const prog = getActivityProgress(activity.id);
      let progressId: string = prog?.id || '';
      if (!progressId) {
        progressId = await upsertActivityProgress(assignmentId!, activity.id, 'in_progress');
        setProgress(prev => [...prev, {
          id: progressId as string,
          assignmentId: assignmentId!,
          activityId: activity.id,
          status: 'in_progress' as ActivityStatus,
          startedAt: new Date().toISOString(),
          completedAt: null,
        } as ProgramActivityProgress]);
      }
      await upsertActivityResponse(progressId, activity.id, text);
      await handleComplete(activity);
    } catch (err) {
      console.error('Error saving reflection:', err);
      alert('Failed to save reflection.');
    } finally {
      setSubmitting(null);
    }
  };

  const handleUpload = async (activity: ProgramActivity, file: File) => {
    setUploading(activity.id);
    try {
      const { path, filename } = await uploadProgramFile(activity.programId, file);
      const prog = getActivityProgress(activity.id);
      let progressId: string = prog?.id || '';
      if (!progressId) {
        progressId = await upsertActivityProgress(assignmentId!, activity.id, 'in_progress');
        setProgress(prev => [...prev, {
          id: progressId as string,
          assignmentId: assignmentId!,
          activityId: activity.id,
          status: 'in_progress' as ActivityStatus,
          startedAt: new Date().toISOString(),
          completedAt: null,
        } as ProgramActivityProgress]);
      }
      await upsertActivityResponse(progressId, activity.id, '', path, filename);
      await handleComplete(activity);
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload file.');
    } finally {
      setUploading(null);
    }
  };

  if (loading) return <LoadingSpinner message="Loading program..." />;

  const total = program?.activities.length || 0;
  const completed = progress.filter(p => p.status === 'completed').length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/dashboard/programs')} className="text-chocolate-400 hover:text-chocolate-700 text-sm mb-2">
            ← My Programs
          </button>
          <h1 className="freud-heading-lg text-chocolate-900">{program?.title || 'Program'}</h1>
          {program?.description && (
            <p className="text-sm text-chocolate-500 mt-1">{program.description}</p>
          )}
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-chocolate-500 mb-1">
              <span>{completed} of {total} activities completed</span>
              <span>{pct}%</span>
            </div>
            <div className="w-full h-2 bg-cream-200 rounded-full overflow-hidden">
              <div className="h-full bg-sage-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-4">
        {assignmentCompletedAt && (
          <div className="freud-card-elevated p-4 bg-success-50 border-success-200">
            <div className="flex items-center gap-2 text-green-700">
              <Check className="w-5 h-5" />
              <span className="font-medium">Program completed! Great work.</span>
            </div>
          </div>
        )}

        {program?.activities.map((activity, index) => {
          const prog = getActivityProgress(activity.id);
          const status = prog?.status || 'not_started';
          const isLocked = isActivityLocked(index);
          const isComplete = status === 'completed';
          const isOverdue = activity.dueDate && !isComplete && new Date(activity.dueDate) < new Date();
          const response = responses.find(r => r.activityId === activity.id);

          return (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`freud-card-elevated p-6 ${isLocked ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {isLocked ? (
                    <div className="w-8 h-8 rounded-full bg-cream-200 flex items-center justify-center">
                      <Lock className="w-4 h-4 text-chocolate-400" />
                    </div>
                  ) : isComplete ? (
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                  ) : isOverdue ? (
                    <div className="w-8 h-8 rounded-full bg-warm-100 flex items-center justify-center">
                      <AlertCircle className="w-4 h-4 text-warm-600" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full border-2 border-cream-300 flex items-center justify-center text-xs font-bold text-chocolate-400">
                      {index + 1}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-sage-100 text-sage-700 font-medium">
                      {ACTIVITY_TYPE_LABELS[activity.activityType]}
                    </span>
                    <h3 className="text-sm font-medium text-chocolate-900">{activity.title}</h3>
                    {isOverdue && (
                      <span className="text-xs text-warm-600 font-medium">Overdue</span>
                    )}
                    {activity.dueDate && !isOverdue && !isComplete && (
                      <span className="text-xs text-chocolate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Due: {new Date(activity.dueDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {activity.instructions && (
                    <p className="text-sm text-chocolate-600 mb-4">{activity.instructions}</p>
                  )}

                  {!isLocked && !isComplete && (
                    <>
                      {/* Resource link */}
                      {activity.resourceUrl && ['watch', 'listen', 'external_link', 'read'].includes(activity.activityType) && (
                        <a
                          href={activity.resourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-sage-600 hover:text-sage-700 mb-3"
                        >
                          <ChevronRight className="w-4 h-4" />
                          Open resource
                        </a>
                      )}

                      {/* AI Chat / Voice */}
                      {(activity.activityType === 'ai_chat' || activity.activityType === 'ai_voice') && (
                        <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center gap-2 text-sm text-blue-700">
                            {activity.activityType === 'ai_chat' ? <MessageSquare className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                            <span>Open {activity.activityType === 'ai_chat' ? 'AI Chat' : 'AI Voice'} from your dashboard to complete this activity.</span>
                          </div>
                        </div>
                      )}

                      {/* Written Reflection */}
                      {activity.activityType === 'written_reflection' && (
                        <div className="mb-3">
                          <textarea
                            value={reflectionTexts[activity.id] || ''}
                            onChange={e => setReflectionTexts(prev => ({ ...prev, [activity.id]: e.target.value }))}
                            placeholder="Write your reflection here..."
                            rows={4}
                            className="w-full px-4 py-2.5 rounded-lg border border-cream-300 bg-white text-chocolate-900 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400 resize-none text-sm"
                          />
                          <button
                            onClick={() => handleSaveReflection(activity)}
                            disabled={submitting === activity.id}
                            className="mt-2 freud-btn-primary inline-flex items-center gap-2 text-sm"
                          >
                            <Check className="w-4 h-4" />
                            {submitting === activity.id ? 'Saving...' : 'Save & Complete'}
                          </button>
                        </div>
                      )}

                      {/* Upload */}
                      {activity.activityType === 'upload' && (
                        <div className="mb-3">
                          <label className="block">
                            <input
                              type="file"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) handleUpload(activity, file);
                              }}
                              disabled={uploading === activity.id}
                              className="block w-full text-sm text-chocolate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-sage-50 file:text-sage-700 hover:file:bg-sage-100"
                            />
                          </label>
                          {uploading === activity.id && (
                            <p className="text-xs text-chocolate-400 mt-1">Uploading...</p>
                          )}
                        </div>
                      )}

                      {/* Mark Complete button for simple types */}
                      {['task', 'read', 'watch', 'listen', 'external_link', 'assessment', 'nlpos_activity', 'ai_chat', 'ai_voice', 'custom'].includes(activity.activityType) && activity.activityType !== 'written_reflection' && activity.activityType !== 'upload' && (
                        <button
                          onClick={() => handleComplete(activity)}
                          disabled={submitting === activity.id}
                          className="freud-btn-primary inline-flex items-center gap-2 text-sm"
                        >
                          <Check className="w-4 h-4" />
                          {submitting === activity.id ? 'Marking...' : 'Mark Complete'}
                        </button>
                      )}
                    </>
                  )}

                  {isComplete && response && response.responseText && (
                    <div className="mt-2 p-3 bg-cream-50 rounded-lg">
                      <p className="text-xs text-chocolate-400 mb-1">Your response:</p>
                      <p className="text-sm text-chocolate-700 whitespace-pre-wrap">{response.responseText}</p>
                    </div>
                  )}

                  {isComplete && response && response.uploadFilename && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-sage-600">
                      <Upload className="w-4 h-4" />
                      {response.uploadFilename}
                    </div>
                  )}

                  {isLocked && (
                    <p className="text-xs text-chocolate-400">Complete the previous activity to unlock this one.</p>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}

        {program && program.activities.length === 0 && (
          <div className="freud-card-elevated p-12 text-center">
            <FileText className="w-12 h-12 text-chocolate-300 mx-auto mb-4" />
            <p className="text-sm text-chocolate-500">This program has no activities yet.</p>
          </div>
        )}
      </main>
    </div>
  );
}
